import { useSignMessage } from '@pancakeswap/wagmi'
import Link from 'next/link'
import { useState } from 'react'
import styled from 'styled-components'
import useSWR from 'swr'
import { api, errorText } from './api'
import { SectionTitle, Surface } from './Identity'
import { PersonShield, chipText, shortToken } from './PersonTicks'
import { Btn, Muted } from './ui'

/**
 * Token team invitations on the wallet's own /u (Bez, 2026-10-04). A project that lists this wallet in its info (wallet + role)
 * waits for one free signature here: "Confirm you are <Role> of <TOKEN>". The blue tick shows once the wallet confirmed, CADINU
 * approved the info AND (since 2026-10-09, project info being free) the token carries its own blue or gold tick. Who listed the wallet stays behind the sign-in; without one, only the public count is known.
 */
export interface Invite {
  id: number
  token: string
  symbol: string
  role: string
  roleLabel: string
  state: 'pending' | 'accepted'
  live: boolean
  infoStatus: 'in_review' | 'approved'
}
interface TeamBody { invites: Invite[]; held: number; max: number }

const List = styled.ul`
  list-style: none;
  margin: 12px 0 0;
  padding: 0;
  display: grid;
  gap: 10px;
`
const Item = styled.li<{ $pending: boolean }>`
  display: grid;
  gap: 10px;
  padding: 14px;
  border-radius: 18px;
  border: 1px solid ${({ $pending }) => ($pending ? 'rgba(124, 156, 255, 0.55)' : 'rgba(255, 255, 255, 0.08)')};
  background: ${({ $pending }) =>
    $pending ? 'radial-gradient(120% 120% at 0% 0%, rgba(91, 130, 255, 0.2), transparent 60%), rgba(13, 18, 44, 0.6)' : 'rgba(255, 255, 255, 0.025)'};
  box-shadow: ${({ $pending }) => ($pending ? '0 14px 30px -18px rgba(91, 130, 255, 0.8)' : 'none')};
  min-width: 0;
  .head {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }
  .what {
    min-width: 0;
    display: grid;
  }
  .what strong {
    font-size: 16px;
    line-height: 22px;
    overflow-wrap: anywhere;
  }
  .what a {
    color: inherit;
  }
  .acts {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
`

export default function TeamInvites({ wallet, token, ensureToken, onChange }: { wallet: string; token?: string; ensureToken: () => Promise<string | null>; onChange?: () => void }) {
  const { signMessageAsync } = useSignMessage()
  const count = useSWR<{ pending: number }>(`team-invites:${wallet.toLowerCase()}`, () => api(`team-invites/${wallet}`), { revalidateOnFocus: false, shouldRetryOnError: false })
  const list = useSWR<TeamBody>(token ? `me-team:${token}` : null, () => api<TeamBody>('me/team', { token }), { revalidateOnFocus: false, shouldRetryOnError: false })
  const [busy, setBusy] = useState<number | 'open' | null>(null)
  const [msg, setMsg] = useState<string | null>(null)
  const [sure, setSure] = useState<number | null>(null) // leaving needs a second tap: a left team needs the project to list the wallet again
  const pending = count.data?.pending ?? 0
  const invites = list.data?.invites ?? []
  if (!token && pending === 0) return null
  if (token && !invites.length && !msg) return null

  const run = async (key: number | 'open', job: (tk: string) => Promise<void>) => {
    setBusy(key)
    setMsg(null)
    try {
      const tk = await ensureToken()
      if (!tk) return
      await job(tk)
    } catch (e: any) {
      setMsg(e?.name === 'UserRejectedRequestError' || /reject|denied/i.test(String(e?.message)) ? 'Signature cancelled.' : errorText(e))
    } finally {
      setBusy(null)
    }
  }
  const done = (body: TeamBody) => {
    list.mutate(body, false)
    count.mutate()
    onChange?.()
  }
  const accept = (inv: Invite) =>
    run(inv.id, async (tk) => {
      const n = await api<{ nonce: string; message: string }>(`me/team/${inv.id}/nonce`, { method: 'POST', token: tk })
      const signature = await signMessageAsync({ message: n.message })
      done(await api<TeamBody>(`me/team/${inv.id}/accept`, { method: 'POST', body: { nonce: n.nonce, signature }, token: tk }))
    })
  const leave = (inv: Invite) => {
    if (sure !== inv.id) {
      setSure(inv.id)
      return
    }
    setSure(null)
    run(inv.id, async (tk) => done(await api<TeamBody>(`me/team/${inv.id}/leave`, { method: 'POST', token: tk })))
  }

  return (
    <Surface data-testid="team-invites" aria-labelledby="team-invites-title">
      <SectionTitle as="h2" id="team-invites-title">
        Token team
      </SectionTitle>
      {!token ? (
        <>
          <Muted style={{ marginTop: 8 }}>
            {pending === 1 ? 'A project listed this wallet in its team.' : `${pending} projects listed this wallet in their team.`} Confirm it&apos;s you to see the
            invitation. One free signature, no transaction.
          </Muted>
          <div style={{ marginTop: 12 }}>
            <Btn type="button" onClick={() => run('open', async () => { await list.mutate() })} disabled={busy !== null} data-testid="team-invites-open">
              Confirm it&apos;s you
            </Btn>
          </div>
        </>
      ) : (
        <List>
          {invites.map((inv) => {
            const sym = inv.symbol || shortToken(inv.token)
            const isPending = inv.state === 'pending'
            return (
              <Item key={inv.id} $pending={isPending} data-testid="team-invite" data-state={inv.state} data-live={inv.live}>
                <div className="head">
                  <PersonShield kind="team" size={28} />
                  <div className="what">
                    <strong>{chipText(inv)}</strong>
                    <Muted>
                      {isPending ? (
                        <>
                          <Link href={`/token/${inv.token}`}>{sym}</Link> lists this wallet as {inv.roleLabel}. Confirm it to show the Token team tick on your profile.
                        </>
                      ) : inv.live ? (
                        'Shown on your profile.'
                      ) : (
                        `Confirmed. It shows once ${sym}'s project info is approved and the token has its blue or gold tick.`
                      )}
                    </Muted>
                  </div>
                </div>
                <div className="acts">
                  {isPending && (
                    <Btn type="button" onClick={() => accept(inv)} disabled={busy !== null} data-testid="team-accept">
                      {busy === inv.id ? 'Check your wallet…' : `Confirm I'm ${inv.roleLabel}`}
                    </Btn>
                  )}
                  <Btn type="button" $ghost onClick={() => leave(inv)} disabled={busy !== null} data-testid="team-leave">
                    {sure === inv.id ? 'Tap again to confirm' : isPending ? 'Decline' : 'Leave team'}
                  </Btn>
                </div>
              </Item>
            )
          })}
        </List>
      )}
      {list.data && (
        <Muted style={{ marginTop: 12, fontSize: 13 }} data-testid="team-held">
          A wallet can hold at most {list.data.max} token-team ticks. This one holds {list.data.held}.
        </Muted>
      )}
      {msg && (
        <Muted role="alert" style={{ marginTop: 10, color: '#ffd0d6' }} data-testid="team-error">
          {msg}
        </Muted>
      )}
    </Surface>
  )
}
